import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import iconSrc from '../../utils/iconSrc';
import { sendEvent } from '../../utils/analytics';
import { VisuallyHidden } from '../styled/accessibility';
import { isBrowser } from '../../utils/is-browser';
import { consistentString } from '../../utils/content-formatting';
import { getStorageValue, setStorageValue } from '../../utils/session-storage';

import {
  Wrapper,
  Heading,
  HeadingButton,
  Content,
  IconSVG,
  HeadingText,
  HideShowText,
} from './styles';

const Accordion = ({ header, children, order, handler }) => {
  const accId = consistentString(header).slice(0, 30);
  const [isOpen, setIsOpen] = useState(true);
  const [showControls, setShowControls] = useState(false);

  useEffect(() => {
    setIsOpen(getStorageValue('open', `accordion-${accId}`));
  }, [accId]);

  useEffect(() => {
    if (isBrowser) {
      setShowControls(true);
    }
  }, []);

  const contentProps = {
    id: `accordion-content-${accId}`,
    'aria-labelledby': `accordion-heading-${accId}`,
  };

  const handleClick = () => {
    setStorageValue('open', !isOpen, `accordion-${accId}`);
    sendEvent({
      event: 'accordionInteraction',
      accordionTitle: `${header}`,
      accordionPosition: `${order}`,
      accordionPlacement: 'Content',
      accordionAction: isOpen ? 'Close' : 'Open',
    });
    setIsOpen(!isOpen);

    if (handler) handler();
  };

  return (
    <Wrapper>
      <Heading>
        {showControls ? (
          <HeadingButton
            type="button"
            onClick={handleClick}
            aria-controls={`accordion-content-${accId}`}
            aria-expanded={isOpen}
            $isOpen={isOpen}
          >
            <HeadingText id={`accordion-heading-${accId}`}>
              {header}
            </HeadingText>
            <VisuallyHidden as="span">, </VisuallyHidden>
            <span aria-hidden="true">
              <IconSVG
                cacheRequests
                src={isOpen ? iconSrc('chevron-up') : iconSrc('chevron-down')}
              />
            </span>
            <HideShowText>{isOpen ? 'Hide' : 'Show'}</HideShowText>
            <VisuallyHidden as="span"> this section</VisuallyHidden>
          </HeadingButton>
        ) : (
          <HeadingText id={`accordion-heading-${accId}`}>{header}</HeadingText>
        )}
      </Heading>

      {showControls ? (
        <Content {...contentProps} hidden={!isOpen} role="region">
          {children}
        </Content>
      ) : (
        <Content {...contentProps}>{children}</Content>
      )}
    </Wrapper>
  );
};

Accordion.propTypes = {
  handler: PropTypes.func,
  header: PropTypes.string.isRequired,
  order: PropTypes.number,
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]).isRequired,
};

export default Accordion;
